import moment from 'moment';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { Alert, Button, Grid, Row, Col, Table } from 'react-bootstrap';
import { bindActionCreators, Dispatch } from 'redux';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { User } from '@upstart/harmony/lib/legos/userTypes';
import {
  AttachedFile,
  FinanceResponse,
} from '@upstart/harmony/lib/legos/financingTypes';
import SidebarContainer from 'containers/SidebarContainer';
import { HeaderNavigation, HeaderNotice } from 'components/PencilEditor';
import FinanceResponseService from '../../services/FinanceResponseService';
import FinanceApplicationService from '../../services/FinanceApplicationService';
import dealsActions from '../../redux/deals/actions';

enum StipulationFileKey {
  drivers_license_front = 'driverLicense',
  drivers_license_back = 'driverLicenseBack',
}

enum StipulationFileName {
  drivers_license_front = 'Driver License Front',
  drivers_license_back = 'Driver License Back',
}

// DAPI returns 'miscellaneous' document every time we upload a file without a stipulation. Exclude it from rendering.
const EXCLUDED_CODES = ['miscellaneous'];

const JPEG_CONTENT_TYPE = 'image/jpeg';

interface Params {
  dealId: string;
  responseId: string;
}

interface ApplicationStatusStipulation {
  code?: string;
  title?: string;
}

interface ApplicationStatus {
  stipulations?: ApplicationStatusStipulation[];
  hasEmailBeenVerified?: boolean;
}

interface Props {
  deal?: Deal;
  user?: User;
  isLoading?: boolean;
  actions: {
    dealRequest: ({ dealId }: { dealId: string }) => void;
  };
  dealId: string;
  responseId: string;
}

const getAttachedFileForStipulation = (deal?: Deal, stipulation?: string) => {
  if (!stipulation) {
    return null;
  }
  const attachedFileKey =
    StipulationFileKey[stipulation as keyof typeof StipulationFileKey];
  const attachedFiles = deal?.attachedFiles ?? {};
  const attachedFile = attachedFileKey && attachedFiles[attachedFileKey];
  if (attachedFile) {
    return attachedFile;
  }
  return null;
};

function DocumentUpload(props: Props) {
  const params = useParams<Params>();
  const history = useHistory();
  const isStandaloneUpload = history?.location?.pathname?.includes(
    'document-upload',
  );
  const responseId = params.responseId || props.responseId;
  const dealId = params.dealId || props.dealId;
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const asyncResponse = useAsync<FinanceResponse>(FinanceResponseService.get, [
    responseId,
  ]);

  const asyncUpload = useAsyncCallback<void>(async () => {
    const formData = new FormData();

    const attachedFilesWithSrc = await Promise.all(
      attachedFiles.map(async attachedFile => {
        const { url, ...file } = attachedFile;

        // Fetch file contents from the url
        if (url) {
          const blob = await fetch(url).then(r => r.blob());
          return {
            ...file,
            src: new Blob([blob], { type: JPEG_CONTENT_TYPE }),
          };
        }
        return file;
      }),
    );

    // Attach file sources
    attachedFilesWithSrc.forEach(f => formData.append('files', f.src ?? ''));
    // Attach file data
    formData.append('attachedFiles', JSON.stringify(attachedFilesWithSrc));
    return FinanceApplicationService.uploadFiles(
      asyncResponse.result?.FinanceApplication?.id,
      formData,
    );
  });
  const asyncApplicationStatus = useAsyncCallback<ApplicationStatus>(
    FinanceApplicationService.getApplicationStatus,
  );
  const isLoading =
    asyncUpload.loading ||
    asyncResponse.loading ||
    asyncApplicationStatus.loading ||
    false;
  const stipulations = (
    asyncApplicationStatus.result?.stipulations ?? []
  ).filter(
    stipulation =>
      stipulation.code && !EXCLUDED_CODES.includes(stipulation.code),
  );

  useEffect(() => {
    if (asyncResponse.result) {
      asyncApplicationStatus.execute(asyncResponse.result.applicationId);
    }
    // eslint-disable-next-line
  }, [asyncResponse.result]);

  useEffect(() => {
    if (dealId) {
      props.actions.dealRequest({ dealId });
    }
  }, [dealId, props.actions]);

  useEffect(() => {
    if (asyncResponse.result) {
      // Init attached files from the response
      setAttachedFiles(
        asyncResponse.result.FinanceApplication?.attachedFiles ?? [],
      );
    }
  }, [asyncResponse.result]);

  useEffect(() => {
    if (!isStandaloneUpload) asyncUpload.execute();
  }, [attachedFiles]);

  const renderFile = (file: AttachedFile) => (
    <div className="flex items-center pb-5">
      <i className="glyphicon glyphicon-file pr-5" />
      {file.fileName || 'N/A'}
      <Button
        className="pt-5 no-border"
        disabled={isLoading}
        onClick={() => {
          setAttachedFiles([...attachedFiles].filter(f => f !== file));
        }}
      >
        <i className="glyphicon glyphicon-remove" />
      </Button>
    </div>
  );

  const renderTimestamp = (file?: AttachedFile) => (
    <div>{file?.uploadedAt ? moment(file.uploadedAt).format('LL') : '-'}</div>
  );

  const renderUpload = (label: string, stipulation?: string) => {
    const attachedFileForStipulation = getAttachedFileForStipulation(
      props.deal,
      stipulation,
    );

    return (
      <div className="pb-5">
        <div className="flex items-center">
          <label className="btn btn-link p-0">
            <i className="glyphicon glyphicon-upload pr-5" /> {label}
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={e => {
                const fileItem = e.target.files?.item?.(0);
                if (fileItem) {
                  const reader = new FileReader();

                  // Read file
                  reader.onload = event => {
                    const result = event.target?.result as ArrayBuffer;
                    const blob = new Blob([new Uint8Array(result)], {
                      type: fileItem.type,
                    });
                    setAttachedFiles([
                      // Replace the file if stipulation name is provided
                      ...attachedFiles.filter(
                        f => !stipulation || f.stipulation !== stipulation,
                      ),
                      {
                        src: blob,
                        fileName: fileItem?.name,
                        contentType: fileItem?.type,
                        stipulation,
                      },
                    ]);
                  };

                  reader.readAsArrayBuffer(fileItem);
                  e.target.value = '';
                }
              }}
            />
          </label>
        </div>
        {stipulation && attachedFileForStipulation && (
          <div
            className="btn btn-link p-0"
            onClick={() => {
              setAttachedFiles([
                ...attachedFiles.filter(
                  f => !stipulation || f.stipulation !== stipulation,
                ),
                {
                  fileName:
                    StipulationFileName[
                      stipulation as keyof typeof StipulationFileName
                    ] || stipulation,
                  url: attachedFileForStipulation,
                  contentType: JPEG_CONTENT_TYPE,
                  stipulation,
                },
              ]);
            }}
          >
            Use previously scanned
          </div>
        )}
      </div>
    );
  };

  const renderStipulationRow = (
    applicationStipulation: ApplicationStatusStipulation,
  ) => {
    const stipulationType = applicationStipulation.code;
    const file = attachedFiles.find(f => f.stipulation === stipulationType);

    return (
      <tr key={stipulationType}>
        <td>{applicationStipulation.title}</td>
        <td>
          {file
            ? renderFile(file)
            : renderUpload('Upload Document', stipulationType)}
        </td>
        <td>{renderTimestamp(file)}</td>
      </tr>
    );
  };

  const renderOtherDocumentsRow = () => {
    // Extract files that are not stipulations
    const files = attachedFiles.filter(file => isEmpty(file.stipulation));

    if (files.length === 0) {
      return (
        <tr className="other-documents-row">
          <td>Other Documents</td>
          <td>{renderUpload('Upload Other Document')}</td>
          <td>-</td>
        </tr>
      );
    }

    return files.map((file, i) => {
      const isFirstRow = i === 0;
      const className = classNames({ 'no-border': !isFirstRow });
      return (
        <tr className="other-documents-row" key={file.url ?? ''}>
          <td className={className}>{isFirstRow ? 'Other Documents' : ''}</td>
          <td className={className}>
            {renderFile(file)}{' '}
            {i === files.length - 1
              ? renderUpload('Upload Other Document')
              : null}
          </td>
          <td className={className}>{renderTimestamp(file)}</td>
        </tr>
      );
    });
  };

  const renderDocumentUploader = () => (
    <div
      className={classNames(
        'upstart-offer-container',
        isStandaloneUpload && 'standalone-upload-container',
      )}
    >
      {isLoading && <Alert>Loading...</Alert>}
      {(asyncResponse.error || asyncApplicationStatus.error) && (
        <Alert bsStyle="danger">
          Failed to load application. Please try again.
        </Alert>
      )}
      {asyncUpload.error && (
        <Alert bsStyle="danger">
          Failed to save documents. Please try again.
        </Alert>
      )}
      {asyncApplicationStatus.result && (
        <Grid>
          <Row>
            <Col sm={12}>
              <h4 className="b pt-15 pb-15">Required Document Upload</h4>
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Table className="pt-15 document-upload-table">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Documents Uploaded</th>
                    <th>Date Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {stipulations.map(renderStipulationRow)}
                  {renderOtherDocumentsRow()}
                </tbody>
              </Table>
            </Col>
          </Row>
          {isStandaloneUpload && (
            <Row>
              <Col
                className="pt-15"
                xs={12}
                sm={3}
                smOffset={3}
                md={2}
                mdOffset={4}
              >
                <Button
                  className="cancel-button"
                  onClick={() => {
                    history.push('/upstart-loan-status');
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col className="pt-15" xs={12} sm={3} md={2}>
                <Button
                  bsStyle="primary"
                  className="submit-button"
                  disabled={isLoading}
                  onClick={() => {
                    asyncUpload.execute().then(() => {
                      history.push(`/pencil/${props.deal?.id}`);
                    });
                  }}
                >
                  Save Documents
                </Button>
              </Col>
            </Row>
          )}
        </Grid>
      )}
    </div>
  );

  return isStandaloneUpload ? (
    <SidebarContainer>
      <div className="upstart-offer-header">
        <HeaderNavigation paymentsHref={`/pencil/${params.dealId}`} />
        <HeaderNotice deal={props.deal} user={props.user} />
      </div>
      {renderDocumentUploader()}
    </SidebarContainer>
  ) : (
    renderDocumentUploader()
  );
}

// eslint-disable-next-line
const mapStateToProps = (state: any) => ({
  isLoading: state.deals.fetching,
  deal: state.deals.deal,
  user: state.deals.user,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators({ ...dealsActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(DocumentUpload);
